import React, { useState, useEffect, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Modal,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import RefreshIcon from '@mui/icons-material/Refresh'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

import OrderCard from '../components/Order/OrderCard'
import OrdersData from '../components/Order/OrdersData'
import OrderComponent from '../components/Order/Order'

import {
  getAvailableRiders,
  updateStatus,
  markOrderPrepared,
  updateOrderStatus,
  assignRider,
  getConfiguration
} from '../apollo'

// Vendor-aware active orders query (uses vendor param instead of restaurantId)
// All orders (including delivered/cancelled) across vendor's restaurants
const GET_ORDERS_BY_VENDOR = gql`
  query GetOrdersByVendor($vendor: ID, $page: Int, $rows: Int) {
    getOrdersByVendor(vendor: $vendor, page: $page, rows: $rows) {
      _id
      orderId
      orderStatus
      orderAmount
      walletAmountUsed
      deliveryCharges
      serviceFee
      tipping
      serverTip
      driverTip
      taxationAmount
      paidAmount
      paymentStatus
      paymentMethod
      createdAt
      isActive
      isPickedUp
      isSelfDelivery
      isCatering
      deliveryAddress { deliveryAddress latitude longitude location { coordinates } }
      items { _id title quantity food variation { _id title price discounted } addons { _id title additionalCost options { _id title price quantity addons { title additionalCost options { title price quantity } } } } specialInstructions }
      user { _id name phone email }
      rider { _id name phone pictureUrl driverType available location { coordinates } }
      restaurant { _id timezone name location { coordinates } }
      pickupReceiptImage
      deliveryProofImage
      preparedAt
      reason
      firstDeliveryOrderId
      fdDriverName
      fdDriverPhone
      fdDriverPhoto
      fdDriverTransport
      fdPickupEta
      fdDropoffEta
      fdDropoffPhoto
      fdTrackerLink
      fdDspTrackerLink
      supportTicketId
      supportTicketStatus
      refundedItems
      refundedAmount
      promotion { _id code discountAmount }
      assignedStaff { _id firstName lastName role }
    }
  }
`
const GET_AVAILABLE_RIDERS = gql`${getAvailableRiders}`
const UPDATE_STATUS = gql`${updateStatus}`
const MARK_PREPARED = gql`${markOrderPrepared}`
const UPDATE_ORDER_STATUS = gql`${updateOrderStatus}`
const ASSIGN_RIDER = gql`${assignRider}`
const GET_CONFIGURATION = gql`${getConfiguration}`

const STATUS_TABS = [
  { value: 'ALL', label: 'All Orders' },
  { value: 'PENDING', label: 'New Orders' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'PREPARED', label: 'Ready' },
  { value: 'ASSIGNED', label: 'Rider Out' },
  { value: 'PICKED', label: 'On The Way' },
  { value: 'DELIVERED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REFUNDED', label: 'Refunded' },
]

const DATE_FILTERS = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: 'LAST_WEEK', label: 'Last Week' },
  { value: 'THIS_MONTH', label: 'This Month' },
  { value: 'ALL', label: 'All Time' }
]

const STATUS_COLORS = {
  ALL: '#374151',
  PENDING: '#d97706',
  ACCEPTED: '#2563eb',
  PREPARED: '#7c3aed',
  ASSIGNED: '#0891b2',
  PICKED: '#4f46e5',
  DELIVERED: '#16a34a',
  CANCELLED: '#dc2626',
  REFUNDED: '#059669',
}

const filterByDate = (orders, dateFilter) => {
  if (!orders) return []
  if (dateFilter === 'ALL') return orders
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const lastWeek = new Date(today.getTime() - 7 * 86400000)
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  return orders.filter(o => {
    const d = new Date(o.createdAt)
    switch (dateFilter) {
      case 'TODAY': return d >= today
      case 'YESTERDAY': return d >= yesterday && d < today
      case 'LAST_WEEK': return d >= lastWeek
      case 'THIS_MONTH': return d >= thisMonth
      default: return true
    }
  })
}

const filterByStatus = (orders, statusFilter) => {
  if (!orders) return []
  if (statusFilter === 'ALL') return orders
  if (statusFilter === 'REFUNDED') return orders.filter(o => o.supportTicketStatus === 'RESOLVED')
  return orders.filter(o => o.orderStatus === statusFilter)
}

const MerchantOrders = () => {
  const storedUser = JSON.parse(localStorage.getItem('user-enatega') || '{}')
  const isSuperAdmin = storedUser.userType === 'ADMIN' || storedUser.userType === 'SUPER_ADMIN'
  // Super admin navigates into a vendor context → activeVendorId is set; vendor uses their own vendorId
  const vendorId = isSuperAdmin ? (localStorage.getItem('activeVendorId') || null) : localStorage.getItem('vendorId')

  const [viewMode, setViewMode] = useState('live')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [dateFilter, setDateFilter] = useState('ALL')
  const [detailsModal, setDetailModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [riderDialog, setRiderDialog] = useState(false)
  const [assigningOrder, setAssigningOrder] = useState(null)
  const [selectedRider, setSelectedRider] = useState('')
  const [cancelDialog, setCancelDialog] = useState(false)
  const [cancellingOrder, setCancellingOrder] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }

  const { data: configData } = useQuery(GET_CONFIGURATION)
  const currencySymbol = configData?.configuration?.currencySymbol || '$'

  const {
    data: vendorOrdersData,
    loading: vendorOrdersLoading,
    refetch: refetchAll
  } = useQuery(GET_ORDERS_BY_VENDOR, {
    variables: vendorId ? { vendor: vendorId, page: 0, rows: 9999 } : { page: 0, rows: 9999 },
    fetchPolicy: 'network-only',
    pollInterval: 30000,
    skip: !isSuperAdmin && !vendorId
  })

  const { data: ridersData } = useQuery(GET_AVAILABLE_RIDERS)

  // Keep selected order in sync
  useEffect(() => {
    if (!selectedOrder || !vendorOrdersData?.getOrdersByVendor) return
    const updated = vendorOrdersData.getOrdersByVendor.find(o => o._id === selectedOrder._id)
    if (updated) setSelectedOrder(updated)
  }, [vendorOrdersData])

  const [mutateStatus] = useMutation(UPDATE_STATUS, {
    onCompleted: () => { showSnackbar('Order status updated'); refetchAll() },
    onError: err => showSnackbar(err.message, 'error')
  })
  const [mutateMarkPrepared] = useMutation(MARK_PREPARED, {
    onCompleted: () => { showSnackbar('Order marked ready'); refetchAll() },
    onError: err => showSnackbar(err.message, 'error')
  })

  const [mutateCancelOrder] = useMutation(UPDATE_ORDER_STATUS, {
    onCompleted: () => { showSnackbar('Order cancelled'); refetchAll() },
    onError: err => showSnackbar(err.message, 'error')
  })

  const [mutateAssignRider] = useMutation(ASSIGN_RIDER, {
    onCompleted: () => {
      showSnackbar('Rider assigned successfully')
      setRiderDialog(false); setAssigningOrder(null); setSelectedRider('')
      refetchAll()
    },
    onError: err => showSnackbar(err.message, 'error')
  })

  // All vendor orders (incl. delivered/cancelled) — used for live card view
  const allVendorOrders = (vendorOrdersData?.getOrdersByVendor || []).filter(o => o.orderStatus !== 'PAYMENT_PENDING')

  const stats = useMemo(() => ({
    new: allVendorOrders.filter(o => o.orderStatus === 'PENDING').length,
    active: allVendorOrders.filter(o => ['ACCEPTED', 'PREPARED', 'ASSIGNED', 'PICKED'].includes(o.orderStatus)).length,
    completed: allVendorOrders.filter(o => o.orderStatus === 'DELIVERED').length,
    cancelled: allVendorOrders.filter(o => o.orderStatus === 'CANCELLED').length,
    refunded: allVendorOrders.filter(o => o.supportTicketStatus === 'RESOLVED').length,
    total: allVendorOrders.length
  }), [allVendorOrders])

  const filteredLiveOrders = useMemo(
    () => filterByStatus(filterByDate(allVendorOrders, dateFilter), statusFilter),
    [allVendorOrders, dateFilter, statusFilter]
  )

  const handleViewDetail = order => { setSelectedOrder(order); setDetailModal(true) }
  const handleAccept = order => { mutateStatus({ variables: { id: order._id, orderStatus: 'ACCEPTED' } }) }
  const handleMarkPrepared = order => { mutateMarkPrepared({ variables: { id: order._id } }) }
  const handleCancel = order => { setCancellingOrder(order); setCancelReason(''); setCancelDialog(true) }
  const handleConfirmCancel = () => {
    if (!cancelReason.trim()) { showSnackbar('Please provide a cancellation reason', 'warning'); return }
    mutateCancelOrder({ variables: { id: cancellingOrder._id, status: 'CANCELLED', reason: cancelReason } })
    setCancelDialog(false); setCancellingOrder(null); setCancelReason('')
  }
  const handleAssignRider = order => { setAssigningOrder(order); setSelectedRider(''); setRiderDialog(true) }
  const handleConfirmAssignRider = () => {
    if (!selectedRider) { showSnackbar('Please select a rider', 'warning'); return }
    mutateAssignRider({ variables: { id: assigningOrder._id, riderId: selectedRider } })
  }

  const getStatusChipSx = value => {
    const isActive = statusFilter === value
    const color = STATUS_COLORS[value] || '#374151'
    return {
      cursor: 'pointer',
      fontWeight: isActive ? 700 : 500,
      bgcolor: isActive ? color : 'transparent',
      color: isActive ? 'white' : color,
      border: `1.5px solid ${color}`,
      '&:hover': { bgcolor: color, color: 'white' },
      transition: 'all 0.15s ease'
    }
  }

  return (
    <>
      {/* Hero banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)',
        px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden'
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReceiptLongIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>Orders</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Live orders across all your restaurants</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(22,163,74,0.2)', border: '1px solid rgba(22,163,74,0.4)', borderRadius: '20px', px: 1.5, py: 0.5 }}>
              <FiberManualRecordIcon sx={{ fontSize: 8, color: '#4ade80' }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#4ade80', fontWeight: 700 }}>LIVE</Typography>
            </Box>
            <Button
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => refetchAll()}
              variant="outlined"
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', py: 0.5, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Stats Bar */}
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'New Orders', value: stats.new, color: '#d97706', bg: '#fef3c7', border: '#fde68a', filter: 'PENDING' },
            { label: 'Active Orders', value: stats.active, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', filter: null },
            { label: 'Completed', value: stats.completed, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', filter: 'DELIVERED' },
            { label: 'Cancelled', value: stats.cancelled, color: '#dc2626', bg: '#fef2f2', border: '#fecaca', filter: 'CANCELLED' },
            { label: 'Refunded', value: stats.refunded, color: '#059669', bg: '#ecfdf5', border: '#6ee7b7', filter: 'REFUNDED' },
            { label: 'Total Orders', value: stats.total, color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', filter: 'ALL' }
          ].map(stat => (
            <Grid item xs={6} sm={2} key={stat.label}>
              <Paper
                elevation={0}
                onClick={() => stat.filter && setStatusFilter(stat.filter)}
                sx={{ p: 2, bgcolor: stat.bg, border: `1px solid ${stat.border}`, borderRadius: '14px', cursor: stat.filter ? 'pointer' : 'default', transition: 'box-shadow 0.15s', '&:hover': stat.filter ? { boxShadow: '0 2px 12px rgba(0,0,0,0.1)' } : {} }}
              >
                <Typography variant="h4" fontWeight="bold" sx={{ color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, mt: 0.5, display: 'block' }}>{stat.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* View toggle */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
            <ToggleButton value="live" sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5, '&.Mui-selected': { bgcolor: '#111827', color: 'white', '&:hover': { bgcolor: '#1f2937' } } }}>
              <ViewModuleIcon sx={{ mr: 0.5, fontSize: 15 }} />Live View
            </ToggleButton>
            <ToggleButton value="table" sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5, '&.Mui-selected': { bgcolor: '#111827', color: 'white', '&:hover': { bgcolor: '#1f2937' } } }}>
              <TableChartIcon sx={{ mr: 0.5, fontSize: 15 }} />All Orders
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Live View */}
        {viewMode === 'live' && (
          <>
            <Box mb={2}>
              <Box display="flex" flexWrap="wrap" gap={0.75} mb={1.25}>
                {STATUS_TABS.map(tab => {
                  const dateFiltered = filterByDate(allVendorOrders, dateFilter)
                  const count = tab.value === 'ALL' ? dateFiltered.length : filterByStatus(dateFiltered, tab.value).length
                  return (
                    <Chip key={tab.value} label={`${tab.label} (${count})`} onClick={() => setStatusFilter(tab.value)} sx={getStatusChipSx(tab.value)} size="small" />
                  )
                })}
              </Box>
              <Box display="flex" flexWrap="wrap" gap={0.75}>
                {DATE_FILTERS.map(df => (
                  <Chip
                    key={df.value} label={df.label} onClick={() => setDateFilter(df.value)} size="small"
                    sx={{ cursor: 'pointer', bgcolor: dateFilter === df.value ? '#111827' : 'transparent', color: dateFilter === df.value ? 'white' : '#374151', border: '1.5px solid #d1d5db', fontWeight: dateFilter === df.value ? 600 : 400, '&:hover': { bgcolor: '#111827', color: 'white' }, transition: 'all 0.15s ease' }}
                  />
                ))}
              </Box>
            </Box>

            {vendorOrdersLoading ? (
              <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
            ) : filteredLiveOrders.length === 0 ? (
              <Box textAlign="center" py={10} sx={{ bgcolor: '#f9fafb', borderRadius: '16px', border: '1px dashed #d1d5db' }}>
                <ReceiptLongIcon sx={{ fontSize: 52, color: '#e2e8f0', mb: 1 }} />
                <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No orders found</Typography>
                <Typography sx={{ color: '#9ca3af', mt: 0.5, fontSize: '0.875rem' }}>
                  {statusFilter !== 'ALL' ? 'Try a different status filter' : dateFilter !== 'ALL' ? 'Try selecting a wider date range' : 'No active orders across your restaurants'}
                </Typography>
                <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetchAll()} sx={{ mt: 2 }}>Refresh</Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredLiveOrders.map(order => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={order._id}>
                    <OrderCard
                      order={order}
                      onViewDetail={handleViewDetail}
                      onAccept={handleAccept}
                      onMarkPrepared={handleMarkPrepared}
                      onCancel={handleCancel}
                      onAssignRider={handleAssignRider}
                      isSuperAdmin={false}
                      currencySymbol={currencySymbol}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <OrdersData
            orders={allVendorOrders}
            toggleModal={handleViewDetail}
            loading={vendorOrdersLoading}
            selected={selectedOrder}
            updateSelected={setSelectedOrder}
            subscribeToMore={() => {}}
            isSuperAdmin={false}
          />
        )}
      </Container>

      {/* Order Detail Modal */}
      <Modal open={detailsModal} onClose={() => { setDetailModal(false); setSelectedOrder(null) }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Box sx={{ bgcolor: 'white', borderRadius: 2, width: { xs: '100%', sm: '85%', md: '65%', lg: '50%' }, maxHeight: '90vh', overflowY: 'auto', outline: 'none' }}>
          <OrderComponent order={selectedOrder} onClose={() => setDetailModal(false)} onStatusChange={() => refetchAll()} availableRiders={ridersData?.availableRiders || []} />
        </Box>
      </Modal>

      {/* Rider Assignment Dialog */}
      <Dialog open={riderDialog} onClose={() => setRiderDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Assign Rider — #{assigningOrder?.orderId}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>Select an available rider to deliver this order.</Typography>
          <FormControl fullWidth>
            <InputLabel>Select Rider</InputLabel>
            <Select value={selectedRider} label="Select Rider" onChange={e => setSelectedRider(e.target.value)}>
              {(ridersData?.availableRiders || []).map(rider => (
                <MenuItem key={rider._id} value={rider._id}>
                  {rider.name}
                  {rider.phone && <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>{rider.phone}</Typography>}
                </MenuItem>
              ))}
              {!(ridersData?.availableRiders?.length) && <MenuItem disabled>No available riders right now</MenuItem>}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRiderDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAssignRider} disabled={!selectedRider} sx={{ bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' } }}>Assign Rider</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>Cancel Order — #{cancellingOrder?.orderId}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Reason for cancellation" placeholder="e.g. Customer requested cancellation" value={cancelReason} onChange={e => setCancelReason(e.target.value)} multiline rows={3} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialog(false)}>Go Back</Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel}>Confirm Cancellation</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  )
}

export default withTranslation()(MerchantOrders)
